import { Box, Grid, Text } from '@chakra-ui/react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';

import { ResourceType } from '../../_openapi/portfolio-optimizer';
import { usePositions } from '../../api/position';
import { positionListColumns } from '../../Position/positionListColumns';
import { MoneyCell } from '../../widgets/Table/MoneyCell';
import { NumberCell } from '../../widgets/Table/NumberCell';
import { Pagination } from '../../widgets/Table/Pagination';
import { Table } from '../../widgets/Table/Table';
import { TableControls } from '../../widgets/Table/TableControls';
import { useTableColumnSettings } from '../../widgets/Table/useTableColumnSettings';
import { useTableState } from '../../widgets/Table/useTableState';

export const AssetPositionsTable = ({ sId }: { sId: string }) => {
    const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableState({
        columns,
        initialSort: [{ id: 'marketValue', desc: true }],
        initialFilters: [{ id: 'sId', value: sId }],
    });

    const { data, isLoading } = usePositions({ dataVariant: 'preTrade', pagination, columnFilters, sorting });

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } =
        useTableColumnSettings('assetPositions');

    const table = useReactTable({
        data: data?.positions || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
    });

    const aggregates = useMemo(() => {
        return data?.positions.reduce(
            (result, position) => {
                return {
                    quantity: Number(result.quantity) + Number(position.base.quantity),
                    marketValue: Number(result.marketValue) + Number(position.base.marketValue),
                    nav: Number(result.nav) + Number(position.base.nav),
                };
            },
            { quantity: 0, marketValue: 0, nav: 0 },
        );
    }, [data]);

    return (
        <Box>
            <Grid templateColumns="max-content max-content" columnGap={6}>
                <Text>Total Quantity</Text>
                <NumberCell value={aggregates?.quantity} />
                <Text>Total Market Value</Text>
                <MoneyCell value={aggregates?.marketValue} />
                <Text>Total NAV of Portfolios</Text>
                <MoneyCell value={aggregates?.nav} />
            </Grid>
            <Box>
                <TableControls
                    table={table}
                    resourceType={ResourceType.POSITION}
                    metaData={{ positionType: 'pre_trade' }}
                />
                <Table table={table} allowXOverflow isLoading={isLoading} />
                <Pagination table={table} totalItems={data?.totalNr} />
            </Box>
        </Box>
    );
};

const EXCLUDE_COLUMNS = [
    'sId',
    'description',
    'assetClass',
    'assetType',
    'subAssetType',
    'scId',
    'coId',
    'priceMultiplier',
    'contractMultiplier',
    'symbol',
    'primaryExchange',
    'cusip',
    'isin',
    'sedol',
    'country',
    'mic',
    'gicsCode',
    'gicsIndustry',
    'gicsSector',
    'issuer',
    'coupon',
    'maturityDate',
    'issueDate',
    'firstNoticeDate',
    'settlementMethod',
    'minTick',
    'putCall',
    'underlyingSymbol',
    'ndvrChainId',
];
const columns = positionListColumns.filter((col) => col.id && !EXCLUDE_COLUMNS.includes(col.id));
